import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, useMailboxes } from '../../lib/api';

/** Customer-facing mailbox list. Email + Reconnect button, nothing else.
 * Reconnect calls the same /reoauth endpoint the operator UI uses — the
 * backend allows write-scoped principals (logged-in users included) to
 * trigger this. Password reveal / reset are operator-only and don't
 * appear here. */
export default function UserMailboxes() {
  const { id = '' } = useParams();
  const { data: rows } = useMailboxes(id);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [err, setErr] = useState<string | null>(null);

  const filtered = (rows || []).filter(m => !q || m.email.toLowerCase().includes(q.toLowerCase()));

  async function reconnect(email: string) {
    setBusy(b => ({ ...b, [email]: true })); setErr(null);
    try { await api.reoauthOne(id, email); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(b => ({ ...b, [email]: false })); }
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center gap-2 mb-3">
        <input
          className="input max-w-xs text-sm"
          placeholder="search email"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <span className="text-xs text-dim">{filtered.length} / {rows?.length ?? 0}</span>
      </div>
      {err && <div className="mb-3 text-err text-xs">{err}</div>}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg sticky top-0">
            <tr className="text-left text-[10px] uppercase tracking-wider text-dim">
              <th className="p-3">Email</th>
              <th className="p-3 w-32 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(m => (
              <tr key={m.email} className="border-t border-line/40">
                <td className="p-3 font-mono text-xs">{m.email}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => reconnect(m.email)}
                    disabled={!!busy[m.email]}
                    className="btn-primary text-xs disabled:opacity-50"
                  >
                    {busy[m.email] ? 'Reconnecting…' : 'Reconnect'}
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={2} className="p-6 text-center text-dim text-xs">No mailboxes yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
